import { EXTERNAL_MCP_AUTH_METHOD_OPTIONS } from 'lauria/constants/externalMcpAuthMethod'
import { ExternalMcpFormValues } from 'lauria/types/externalMcp'
import { MouseEvent, useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type UseExternalMcpFormModalParams = {
  open: boolean
  mode: 'create' | 'edit'
  initialValues?: Pick<ExternalMcpFormValues, 'authMethod' | 'url'>
  onClose: () => void
  onSubmit: (values: ExternalMcpFormValues) => void
}

export const useExternalMcpFormModal = ({
  open,
  mode,
  initialValues,
  onClose,
  onSubmit,
}: UseExternalMcpFormModalParams) => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<ExternalMcpFormValues>({
    defaultValues: {
      authMethod: '',
      url: '',
      secret: '',
    },
  })

  useEffect(() => {
    if (!open) {
      return
    }

    reset({
      authMethod: initialValues?.authMethod ?? '',
      url: initialValues?.url ?? '',
      secret: '',
    })
  }, [initialValues, open, reset])

  const hide = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      onClose()
    },
    [onClose]
  )

  const submitForm = handleSubmit((data) => {
    if (!data.authMethod) {
      setError('authMethod', { type: 'required' })
    }

    if (!data.url.trim()) {
      setError('url', { type: 'required' })
    }

    if (mode === 'create' && !data.secret.trim()) {
      setError('secret', { type: 'required' })
    }

    if (
      !data.authMethod ||
      !data.url.trim() ||
      (mode === 'create' && !data.secret.trim())
    ) {
      return
    }

    onSubmit({
      authMethod: data.authMethod,
      url: data.url.trim(),
      secret: mode === 'create' ? data.secret.trim() : '',
    })
    onClose()
  })

  const authMethodOptions = useMemo(
    () =>
      EXTERNAL_MCP_AUTH_METHOD_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    [t]
  )

  const modalTitle =
    mode === 'create'
      ? t('prompts.form.advanced.mcp.modal.title')
      : t('prompts.form.advanced.mcp.modal.editTitle')

  const submitLabel =
    mode === 'create'
      ? t('prompts.form.advanced.mcp.modal.submit')
      : t('prompts.form.advanced.mcp.modal.editSubmit')

  return {
    register,
    errors,
    submitForm,
    hide,
    authMethodOptions,
    modalTitle,
    submitLabel,
    showSecretField: mode === 'create',
  }
}
